import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Typography, Space, Form, Select, DatePicker } from 'antd';
import currency from 'currency.js';
import { ApplicationUIContext } from '@yuhuinc/ui-library';
import { InfoCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { prorateRent, getLeaseTermPricings } from '../store/actions';
import { getTranslatedString } from 'helpers/i18n';
import { toFriendlyDateString } from 'helpers/Formatter';
import isEmpty from 'lodash/isEmpty';
import { startOfDay, endOfDay, isAfter, isBefore } from 'date-fns';
import { featureEnabled } from 'helpers/ToggleBot';

const { Title } = Typography;

const StyledSpace = styled(Space)`
  width: 100%;

  .ant-picker {
    width: 100%;
  }
`;

const StyledRow = styled.div`
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: 16px;

  > * {
    flex: 1;
  }
`;

const StyledFormItem = styled(Form.Item)`
  flex-direction: row-reverse !important;
  align-items: baseline;
  gap: 8px;
  width: fit-content;
`;

const validationRules = [
  {
    required: true,
    message: getTranslatedString(
      'landlordPortal.applicationManagement.inviteLeadToApply.requiredField'
    ),
  },
];

const LeaseDates = ({ selectedUnit, disabled, tenancyLaw }) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const form = Form.useFormInstance();
  const values = Form.useWatch([], form);
  const [prorateRentInfo, setProrateRentInfo] = useState();
  const [loadingTermPricing, setLoadingTermPricing] = useState(false);
  const [terms, setTerms] = useState((selectedUnit && selectedUnit.available_lease_terms) || []);
  const [leaseTermPricings, setLeaseTermPricings] = useState({});

  const availabilityDate = selectedUnit?.availability_date || null;
  const selectedApplicant = values?.selectedApplicant;

  const disabledLeaseStartDate = (date) => {
    const startDate = endOfDay(new Date(date));
    const endOfAvailabilityDate = endOfDay(new Date(availabilityDate));

    const isLeaseStartDateBeforeAvailabilityDate = availabilityDate
      ? isBefore(startDate, endOfAvailabilityDate)
      : false;

    const isLeaseStartDateInThePast = isBefore(startDate, startOfDay(new Date()));

    // Disable date if at least one of the conditions is true
    return isLeaseStartDateBeforeAvailabilityDate || isLeaseStartDateInThePast;
  };

  const disabledLeaseEndDate = (date) => {
    const isLeaseEndDateBeforeToLeaseStartDate = values.lease_start_date
      ? isBefore(endOfDay(new Date(date)), endOfDay(new Date(values.lease_start_date)))
      : false;

    return isLeaseEndDateBeforeToLeaseStartDate;
  };

  const disabledMoveInDate = (date) => {
    const moveInDate = endOfDay(new Date(date));
    const endOfAvailabilityDate = endOfDay(new Date(availabilityDate));
    const startDate = values?.lease_start_date ? endOfDay(new Date(values.lease_start_date)) : null;

    const isMoveInDatePriorToAvailabilityDate = availabilityDate
      ? isBefore(moveInDate, endOfAvailabilityDate)
      : false;
    const isMoveInDateAfterLeaseStartDate = startDate ? isAfter(moveInDate, startDate) : false;

    return isMoveInDatePriorToAvailabilityDate || isMoveInDateAfterLeaseStartDate;
  };

  const onLeaseStartDateChange = (leaseStartDate) => {
    // The move-in date defaults to the start lease date as described in AL-1106
    form.setFieldsValue({ early_move_in_date: leaseStartDate });

    // TODO: Use ERP settings to configure
    if (featureEnabled('erp-fetch-term-pricing')) {
      setLoadingTermPricing(true);
      form.setFieldsValue({ lease_term: null });
      getLeaseTermPricings(leaseStartDate, selectedUnit.id)
        .then((res) => {
          const terms = res.lease_term_pricings.map((obj) => obj.durationInMonths);
          setTerms(terms);
          setLeaseTermPricings(res.lease_term_pricings);
          setLoadingTermPricing(false);
        })
        .catch((err) => {
          setLoadingTermPricing(false);
        });
    }
  };

  const onLeaseEndDateChange = (leaseEndDate) => {
    // set the lease term to 'Custom' and rely on the lease_end_date value instead
    form.setFieldsValue({ lease_term: 'Custom' });
  };

  const setMonthlyRentBasedOnTermLength = (termLength) => {
    if (!featureEnabled('erp-fetch-term-pricing') || isEmpty(leaseTermPricings)) return;

    const selectedTermPricing = leaseTermPricings.find(
      (pricing) => pricing.durationInMonths === termLength
    );
    form.setFieldsValue({ monthly_rent: selectedTermPricing.priceInDollars });
  };

  const setLeaseEndDateBasedOnTermLength = (termLength) => {
    if (!termLength) return;
    if (!values.lease_start_date) return;

    form.setFieldsValue({
      lease_end_date: values.lease_start_date.clone().add(termLength, 'months').subtract(1, 'day'),
    });
  };

  const onLeaseTermChange = (termLength) => {
    setMonthlyRentBasedOnTermLength(termLength);
    setLeaseEndDateBasedOnTermLength(termLength);
  };

  useEffect(() => {
    if (values?.monthly_rent && values?.early_move_in_date && values?.lease_start_date) {
      prorateRent({
        total_monthly_amount: values.monthly_rent,
        move_in_date: values.early_move_in_date.clone().startOf('day'),
        lease_start_date: values.lease_start_date.clone().startOf('day'),
      }).then((data) => {
        if (values?.create_prorated_charge) {
          form.setFieldsValue({
            prorated_rent_value: currency(data.prorated_rent_amount, { fromCents: false }),
          });
        }
        setProrateRentInfo(data);
      });
    } else {
      form.setFieldsValue({ prorated_rent_value: currency(0, { fromCents: false }) });
    }
  }, [
    values?.monthly_rent,
    values?.early_move_in_date,
    values?.lease_start_date,
    values?.create_prorated_charge,
  ]);

  useEffect(() => {
    if (!values?.create_prorated_charge) {
      form.setFieldsValue({ prorated_rent_value: currency(0, { fromCents: false }) });
    }
  }, [values?.create_prorated_charge]);

  useEffect(() => {
    if (!isEmpty(tenancyLaw) && values?.monthly_rent) {
      setTerms(tenancyLaw.available_terms);
      const calculationType = tenancyLaw.deposits.find(
        (deposit) => deposit.calculated_type === 'unit'
      );
      const deposit = values.monthly_rent * calculationType.calculalted_percentage / 100

      form.setFieldsValue({ deposit_amount: currency(deposit, { fromCents: false }) });
    }
  }, [tenancyLaw, values?.monthly_rent]);

  return (
    <StyledSpace direction="vertical" size="middle">
      <Title level={isMobile ? 4 : 5} style={{ margin: 0 }}>
        {getTranslatedString(
          'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.leaseDates'
        )}
      </Title>

      <StyledRow isMobile={isMobile}>
        <Form.Item
          rules={validationRules}
          label={getTranslatedString(
            'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.leaseDates.leaseStartDate'
          )}
          name="lease_start_date"
        >
          <DatePicker
            disabledDate={disabledLeaseStartDate}
            onChange={onLeaseStartDateChange}
            disabled={disabled}
          />
        </Form.Item>

        <Form.Item
          rules={validationRules}
          label={getTranslatedString(
            'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.leaseDates.leaseTerm'
          )}
          name="lease_term"
        >
          <Select
            loading={loadingTermPricing}
            disabled={disabled || loadingTermPricing}
            onChange={onLeaseTermChange}
            options={terms.map((value) => ({
              value,
              label: `${value} ${
                value === 1
                  ? getTranslatedString('monthSingular')
                  : getTranslatedString('monthsPlural')
              }`,
            }))}
          />
        </Form.Item>
      </StyledRow>

      <StyledRow isMobile={isMobile}>
        <Form.Item
          label={getTranslatedString(
            'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.leaseDates.leaseEndDate'
          )}
          name="lease_end_date"
        >
          <DatePicker
            disabledDate={disabledLeaseEndDate}
            disabled={disabled || !values?.lease_start_date}
            onChange={onLeaseEndDateChange}
          />
        </Form.Item>

        <Form.Item
          label={getTranslatedString(
            'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.leaseDates.moveInDate'
          )}
          name="early_move_in_date"
          extra={
            selectedApplicant?.lead_card?.target_move_in_date
              ? getTranslatedString(
                  'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.leaseDates.requestedMoveInDate',
                  {
                    requestedMoveInDate: toFriendlyDateString(
                      selectedApplicant.lead_card.target_move_in_date
                    ),
                  }
                )
              : null
          }
          tooltip={{
            title: getTranslatedString(
              'landlordPortal.applicationManagement.inviteLeadToApply.additionalInformation.leaseDates.moveInDate.tooltip'
            ),
            icon: <InfoCircleOutlined />,
          }}
        >
          <DatePicker disabledDate={disabledMoveInDate} disabled={disabled} />
        </Form.Item>
      </StyledRow>

      <div>
        <Form.Item name="create_prorated_charge" hidden />
      </div>
    </StyledSpace>
  );
};

LeaseDates.propTypes = {
  selectedUnit: PropTypes.object,
  disabled: PropTypes.bool,
};

export default LeaseDates;
